import { useMemo, useState } from 'react';

import { useGoogleAuth } from '../providers/Google';
import { useLegacy } from '../providers/Legacy';
import { useMS } from '../providers/MS';
import { GOOGLE_AUTH_TYPE, MS_AUTH_TYPE, LEGACY_AUTH_TYPE } from './constants';

const noop = async () => null;

const CURRENT_PROVIDER_KEY = 'currentProvider';

const useLocalState = (key) => {
  const [state, setState] = useState(() =>
    localStorage.getItem(CURRENT_PROVIDER_KEY)
  );

  useMemo(() => {
    if (localStorage.getItem(key) !== state) {
      localStorage.setItem(key, state);
    }
  }, [state]);
  return [state, setState];
};

const useInitial = (key) => {
  return useState(localStorage.getItem(key))[0];
};

export const useMultiProvider = () => {
  const [currentProvider, setCurrentProvider] =
    useLocalState(CURRENT_PROVIDER_KEY);

  const initialProvider = useInitial(CURRENT_PROVIDER_KEY);
  const googleAuth = useGoogleAuth({
    signInOnLoad: initialProvider === GOOGLE_AUTH_TYPE,
    setCurrentProvider: () => {
      setCurrentProvider(GOOGLE_AUTH_TYPE);
    },
    onNoSession: () => {
      setCurrentProvider(null);
    },
  });

  const legacyAuth = useLegacy({
    signInOnLoad: initialProvider === LEGACY_AUTH_TYPE,
    onNoSession: () => {
      setCurrentProvider(null);
    },
  });

  const msAuth = useMS({
    signInOnLoad: initialProvider === MS_AUTH_TYPE,
    onNoSession: () => {
      setCurrentProvider(null);
    },
  });

  const providers = [googleAuth, msAuth, legacyAuth];
  const loaded = providers.every((provider) => provider.loaded);

  const currentAuth = useMemo(() => {
    switch (currentProvider) {
      case GOOGLE_AUTH_TYPE:
        return googleAuth;
      case LEGACY_AUTH_TYPE:
        return legacyAuth;
      case MS_AUTH_TYPE:
        return msAuth;
      default:
        return null;
    }
  }, [currentProvider, googleAuth, legacyAuth]);

  const login = async (res, type) => {
    switch (type) {
      case LEGACY_AUTH_TYPE:
        await legacyAuth.login(res);
        setCurrentProvider(type);
        break;
      case MS_AUTH_TYPE:
        await msAuth.login(res);
        setCurrentProvider(type);
        break;
    }
  };

  const logout = () => {
    currentAuth.logout();
    setCurrentProvider(null);
  };

  return {
    _name: currentAuth?._name || 'multiProvider',
    _hasError: currentAuth?._hasError || false,
    isAuthenticated: currentAuth?.isAuthenticated || false,
    getAccessToken: currentAuth?.getAccessToken || noop,
    onUnauthorized: currentAuth?.onUnauthorized || noop,
    active: !!currentAuth,
    loaded: loaded,
    login: login,
    logout: logout,
  };
};
